import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Timestamp } from '@angular/fire/firestore';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { Stop } from '../../../../core/models/stop.model';
import { LoadService } from '../../../../core/services/load.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';

import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';

import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import {
  StopStatusType,
  StopStatusOptionMap,
} from '../../../../core/enums/stop-status.enum';
import { EnumLabelPipe } from '../../../../shared/pipes/enum-label.pipe';
import { InputComponent } from '../../../../shared/ui/forms/input/input.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import { DateTimeObject } from '../../../../core/models/datetime.model';
import { StopTypeOptionMap } from '../../../../core/enums/stop-type.enum';
import { ColorVariant } from '../../../../core/enums/types';
import { DocumentViewerService } from '../../../../core/services/document-viewer.service';
import { DatetimePickerComponent } from '../../../../shared/ui/forms/datetime-picker/datetime-picker.component';
import { StorageService } from '../../../../core/services/storage.service';
import { dateInTimezone, guessTimezoneFromState, toStopLocalString, formatDateTimeObject, toStopDateTimeObject, getNowInTimezone } from '../../../../shared/utils/datetime.utils';

import { SelectComponent, SelectOption } from '../../../../shared/ui/forms/select/select.component';

interface StopFormData {
  stop: Stop;
  form: FormGroup;
  isExpanded: boolean;
  isCompleted: boolean;
  isCurrentStop: boolean;
  editingFields: Set<string>;
}

type EditableField =
  | 'dockNumber'
  | 'bol'
  | 'loadImage'
  | 'sealImage'
  | 'temperatureImage'
  | 'pod'
  | 'eta';

@Component({
  selector: 'app-status-update-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    LucideIconComponent,
    TextComponent,
    BadgeComponent,
    EnumLabelPipe,
    InputComponent,
    CheckboxComponent,
    DatetimePickerComponent,
    SelectComponent,
  ],
  templateUrl: './status-update-modal.component.html',
})
export class StatusUpdateModalComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);
  private readonly documentViewer = inject(DocumentViewerService);
  private readonly storageService = inject(StorageService);

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly actionLoading = signal<{ stopNumber: number; action: string } | null>(null);

  protected readonly stopForms = signal<StopFormData[]>([]);
  protected readonly currentStopIndex = signal(0);
  protected readonly rejectingStopNumber = signal<number | null>(null);
  protected readonly rejectionReason = signal<string>('');

  // Granular Document Review State
  protected readonly rejectingDoc = signal<{ stopNumber: number; docKey: string } | null>(null);
  protected readonly docRejectionReason = signal<string>('');
  protected readonly uploadingDoc = signal<{ stopNumber: number; docKey: string; progress: number } | null>(null);

  // Milestone editing and document management state
  protected readonly editingMilestone = signal<{ stopNumber: number; milestone: string } | null>(null);
  protected readonly isSavingMilestone = signal(false);
  protected readonly isSavingDocuments = signal(false);
  protected readonly showingDocUpload = signal<number | null>(null);
  protected readonly showCustomActionTime = signal<Record<number, boolean>>({});
  protected readonly draggingOverDoc = signal<Record<string, boolean>>({});

  protected readonly milestoneDateTimeControl = new FormControl<DateTimeObject>({ date: null, time: null });
  protected readonly milestoneDockControl = new FormControl<string>('');
  protected readonly actionDateTimeControls: Record<number, FormControl<DateTimeObject | null>> = {};

  private getCurrentDateTimeObject(stop?: Stop): DateTimeObject {
    const stopTz = stop?.timezone || (stop?.address?.state ? guessTimezoneFromState(stop.address.state) : undefined);
    return getNowInTimezone(stopTz);
  }

  readonly lumperPayerOptions: SelectOption[] = [
    { value: 'carrier', label: 'Carrier Out-of-Pocket' },
    { value: 'driver', label: 'Driver Out-of-Pocket' },
    { value: 'broker', label: 'Paid by Broker' },
  ];

  // Computed: Check if load is reefer
  protected readonly isReeferLoad = computed(() => {
    const trailerType = this.load.trailerType;
    return trailerType === 'REEFER' || trailerType === 'REEFER_TEAM';
  });

  // Computed: Completed stops count
  protected readonly completedStopsCount = computed(() => {
    return this.stopForms().filter((sf) => sf.isCompleted).length;
  });

  // Computed: Total stops count
  protected readonly totalStopsCount = computed(() => {
    return this.stopForms().length;
  });

  ngOnInit() {
    this.initializeStopForms();
  }

  private initializeStopForms(): void {
    const forms: StopFormData[] = [];
    let foundIncomplete = false;
    let currentIndex = 0;

    this.load.stops.forEach((stop, index) => {
      const isCompleted = this.isStopCompleted(stop);
      const isCurrentStop = !foundIncomplete && !isCompleted;

      if (isCurrentStop) {
        foundIncomplete = true;
        currentIndex = index;
      }

      const form = this.createStopForm(stop);

      if (stop.stopNumber !== null && stop.stopNumber !== undefined) {
        this.actionDateTimeControls[stop.stopNumber] = new FormControl<DateTimeObject | null>(this.getCurrentDateTimeObject(stop));
      }

      forms.push({
        stop,
        form,
        isExpanded: isCurrentStop,
        isCompleted,
        isCurrentStop,
        editingFields: new Set<string>(),
      });
    });

    this.stopForms.set(forms);
    this.currentStopIndex.set(currentIndex);
  }

  private createStopForm(stop: Stop): FormGroup {
    const isPickup = stop.stopType === 'pickup' || stop.stopType === 'both';
    const isDelivery = stop.stopType === 'delivery' || stop.stopType === 'both';

    const form = this.fb.group({
      etaDateTime: [stop.etaDateTime || stop.appointment?.from || { date: null, time: null }],
      dockNumber: [{ value: stop.dockNumber === 'N/A' ? '' : (stop.dockNumber || null), disabled: stop.dockNumber === 'N/A' }],
      noDockNumber: [stop.dockNumber === 'N/A'],

      ...(isPickup && {
        bol: [{ value: stop.documents?.billOfLading?.url === 'N/A' ? null : (stop.documents?.billOfLading?.url || null), disabled: stop.documents?.billOfLading?.url === 'N/A' }],
        noBol: [stop.documents?.billOfLading?.url === 'N/A'],
        loadImage: [{ value: stop.documents?.loadImage?.url === 'N/A' ? null : (stop.documents?.loadImage?.url || null), disabled: stop.documents?.loadImage?.url === 'N/A' }],
        noLoadImage: [stop.documents?.loadImage?.url === 'N/A'],
        sealImage: [{ value: stop.documents?.sealImage?.url === 'N/A' ? null : (stop.documents?.sealImage?.url || null), disabled: stop.documents?.sealImage?.url === 'N/A' }],
        noSealImage: [stop.documents?.sealImage?.url === 'N/A'],
        ...(this.isReeferLoad() && {
          temperatureImage: [{ value: stop.documents?.temperatureImage?.url === 'N/A' ? null : (stop.documents?.temperatureImage?.url || null), disabled: stop.documents?.temperatureImage?.url === 'N/A' }],
          noTemperatureImage: [stop.documents?.temperatureImage?.url === 'N/A'],
        }),
      }),

      ...(isDelivery && {
        pod: [{ value: stop.documents?.proofOfDelivery?.url === 'N/A' ? null : (stop.documents?.proofOfDelivery?.url || null), disabled: stop.documents?.proofOfDelivery?.url === 'N/A' }],
        noPod: [stop.documents?.proofOfDelivery?.url === 'N/A'],
      }),

      lumperFee: [stop.lumperFee ?? null],
      lumperPaidBy: [stop.lumperPaidBy ?? 'carrier'],
      lumperReceipt: [{ value: stop.documents?.lumperReceipt?.url === 'N/A' ? null : (stop.documents?.lumperReceipt?.url || null), disabled: stop.documents?.lumperReceipt?.url === 'N/A' }],
      noLumperReceipt: [stop.documents?.lumperReceipt?.url === 'N/A'],
    });

    // Subscribe to noDockNumber changes
    form.get('noDockNumber')?.valueChanges.subscribe(noDock => {
      const dockControl = form.get('dockNumber');
      if (noDock) {
        dockControl?.disable();
        dockControl?.setValue(null);
      } else {
        dockControl?.enable();
      }
    });

    const wireDocControl = (
      controlName: string,
      noControlName: string,
      docKey: string
    ) => {
      form.get(noControlName)?.valueChanges.subscribe(noDoc => {
        const docControl = form.get(controlName);
        if (noDoc) {
          docControl?.disable({ emitEvent: false });
          docControl?.setValue(null, { emitEvent: false });
          this.autoSaveDocument(stop.stopNumber, docKey, 'N/A');
        } else {
          docControl?.enable({ emitEvent: false });
          if ((stop.documents as any)?.[docKey]?.url === 'N/A') {
            this.autoSaveDocument(stop.stopNumber, docKey, null);
          }
        }
      });

      form.get(controlName)?.valueChanges.subscribe(val => {
        if (typeof val === 'string' && val.trim() !== '' && val !== 'N/A') {
          this.autoSaveDocument(stop.stopNumber, docKey, val.trim());
        }
      });
    };

    if (isPickup) {
      wireDocControl('bol', 'noBol', 'billOfLading');
      wireDocControl('loadImage', 'noLoadImage', 'loadImage');
      wireDocControl('sealImage', 'noSealImage', 'sealImage');
      if (this.isReeferLoad()) {
        wireDocControl('temperatureImage', 'noTemperatureImage', 'temperatureImage');
      }
    }
    if (isDelivery) {
      wireDocControl('pod', 'noPod', 'proofOfDelivery');
    }

    wireDocControl('lumperReceipt', 'noLumperReceipt', 'lumperReceipt');

    const saveLumper = async () => {
      const feeVal = form.get('lumperFee')?.value;
      const parsedFee = feeVal != null && !isNaN(Number(feeVal)) ? Number(feeVal) : null;
      const payerVal = form.get('lumperPaidBy')?.value || 'carrier';

      // Validation rule: receipt requires fee > 0
      const currentReceipt = form.get('lumperReceipt')?.value;
      if (currentReceipt && currentReceipt !== 'N/A' && (!parsedFee || parsedFee <= 0)) {
        this.toastService.show('warning', 'Lumper fee must be greater than $0 when a receipt is attached.', {
          title: 'Fee Required',
          duration: 4000,
        });
        return;
      }

      const stopIndex = this.load.stops.findIndex((s) => s.stopNumber === stop.stopNumber);
      if (stopIndex === -1) return;

      const updatedStops = [...this.load.stops];
      updatedStops[stopIndex] = {
        ...updatedStops[stopIndex],
        lumperFee: parsedFee,
        lumperPaidBy: payerVal,
      };
      this.load.stops = updatedStops;

      try {
        await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));
        const currentCompanyId = (this.load as any).view?.companyId || this.load.carrierId;
        if (currentCompanyId) {
          await this.loadService.syncStopLumpers(
            this.load.id,
            updatedStops,
            currentCompanyId,
            this.load.driver1Id
          );
        }
      } catch (err: any) {
        console.error('Failed to update stop lumper fee:', err);
      }
    };

    form.get('lumperFee')?.valueChanges.subscribe(() => saveLumper());
    form.get('lumperPaidBy')?.valueChanges.subscribe(() => saveLumper());

    return form;
  }

  private isStopCompleted(stop: Stop): boolean {
    return stop.status === 'DEPARTED';
  }

  protected toggleStop(index: number): void {
    this.stopForms.update((forms) => {
      const newForms = [...forms];
      newForms[index] = {
        ...newForms[index],
        isExpanded: !newForms[index].isExpanded,
      };
      return newForms;
    });
  }

  protected toggleFieldEdit(index: number, field: EditableField): void {
    this.stopForms.update((forms) => {
      const newForms = [...forms];
      const editingFields = new Set<EditableField>();

      if (!newForms[index].editingFields.has(field) || newForms[index].editingFields.size > 1) {
        editingFields.add(field);
      }

      newForms[index] = {
        ...newForms[index],
        editingFields,
      };
      return newForms;
    });
  }

  protected async forceCompleteStop(index: number): Promise<void> {
    const stopForm = this.stopForms()[index];
    const stop = stopForm.stop;
    
    this.loadingService.show({
      variant: 'overlay',
      title: 'Completing Stop',
      subtitle: `Completing Stop ${stop.stopNumber}...`,
    });
    
    try {
      const nowDto = this.getCurrentDateTimeObject();
      
      await this.loadService.callAdvanceLoadEvent({
        loadId: this.load.id,
        actingAs: 'admin',
        stopAction: 'DEPART',
        stopNumber: stop.stopNumber!,
        customTimestamp: nowDto,
        reason: 'Force completed by dispatcher',
        performedVia: 'admin_app',
      });
      
      this.toastService.show('success', `Stop ${stop.stopNumber} completed`);
      this.onSuccess?.();
      this.modalService.close();
    } catch (error: any) {
      this.toastService.show('error', error?.message || 'Failed to complete stop');
    } finally {
      this.loadingService.hide();
    }
  }

  viewDocument(url: string | null | undefined, title: string): void {
    if (!url || url === 'N/A') return;
    const documentType: 'pdf' | 'image' = url.toLowerCase().includes('.pdf') ? 'pdf' : 'image';
    import('../../../../shared/ui/document-viewer-modal/document-viewer-modal.component').then(
      ({ DocumentViewerModalComponent }) => {
        this.modalService.open(DocumentViewerModalComponent, {
          data: { url, documentType },
          title: `${title} - #${this.load.externalId || this.load.id}`,
          subtitle: 'View and download document',
          icon: documentType === 'pdf' ? 'FileText' : 'Image',
          size: 'xl',
        });
      }
    );
  }

  isPdfUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf');
  }

  isReviewStage(stop: Stop): boolean {
    return [
      'ARRIVED',
      'CHECKED_IN',
      'WAITING_APPROVAL',
      'REJECT_DOCS',
      'APPROVED',
      'LOADED',
      'UNLOADED',
      'DOCKED',
      'LOADING',
      'UNLOADING',
      'CHECKED_OUT',
    ].includes(stop.status || '');
  }

  replacingDocs = signal<Record<string, boolean>>({});

  isReplacingDoc(stopNumber: number | null | undefined, docKey: string): boolean {
    if (!stopNumber) return false;
    return !!this.replacingDocs()[`${stopNumber}_${docKey}`];
  }

  toggleReplacingDoc(stopNumber: number | null | undefined, docKey: string): void {
    if (!stopNumber) return;
    const key = `${stopNumber}_${docKey}`;
    this.replacingDocs.update((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  protected isFieldEditing(index: number, field: EditableField): boolean {
    return this.stopForms()[index].editingFields.has(field);
  }

  protected hasFieldValue(index: number, field: EditableField): boolean {
    const stopData = this.stopForms()[index];

    if (field === 'dockNumber' && stopData.form.get('noDockNumber')?.value) {
      return true;
    }

    if (['bol', 'loadImage', 'sealImage', 'temperatureImage', 'pod'].includes(field)) {
      const noDocControlName = 'no' + field.charAt(0).toUpperCase() + field.slice(1);
      if (stopData.form.get(noDocControlName)?.value) {
        return true;
      }
    }

    const value = stopData.form.get(field)?.value;
    
    return !!value;
  }

  protected isDocCompleted(index: number, doc: string): boolean {
    const stopData = this.stopForms()[index];
    const noDocControlName = 'no' + doc.charAt(0).toUpperCase() + doc.slice(1);
    return this.hasFieldValue(index, doc as EditableField) || !!stopData.form.get(noDocControlName)?.value;
  }

  protected getFieldDisplayValue(index: number, field: EditableField): string {
    const stopData = this.stopForms()[index];
    const value = stopData.form.get(field)?.value;

    if (!value) return '--';

    if (typeof value === 'string' && value.startsWith('http')) {
      return 'Uploaded';
    }

    return String(value);
  }

  protected getStopIcon(stop: Stop): string {
    if (!stop.stopType) return 'MapPin';
    const stopTypeOption = StopTypeOptionMap[stop.stopType];
    return stopTypeOption?.icon || 'MapPin';
  }

  protected getStopTypeColor(stop: Stop): ColorVariant {
    if (!stop.stopType) return 'secondary';
    const stopTypeOption = StopTypeOptionMap[stop.stopType];
    return (stopTypeOption?.color as ColorVariant) || 'secondary';
  }

  protected getStopStatusColor(stop: Stop): ColorVariant {
    if (!stop.status) return 'secondary';
    const statusOption = StopStatusOptionMap[stop.status];
    return (statusOption?.color as ColorVariant) || 'secondary';
  }

  protected formatTimestamp(value: any, stop?: Stop): string {
    if (!value) return '--';

    // 1. If it's a DateTimeObject { date: string, time: string }
    if (typeof value === 'object' && ('date' in value || 'time' in value) && !value.toDate && value.seconds === undefined && value._seconds === undefined) {
      return formatDateTimeObject(value);
    }

    // 2. Stop timezone or state-based fallback
    const timezone = stop?.timezone || (stop?.address?.state ? guessTimezoneFromState(stop.address.state) : undefined);
    return toStopLocalString(value, timezone);
  }

  protected formatAppointmentTime(
    dateTimeObj: any
  ): string {
    return formatDateTimeObject(dateTimeObj);
  }

  protected toDateTimeObject(value: any, stop?: Stop): DateTimeObject {
    const stopTz = stop?.timezone || (stop?.address?.state ? guessTimezoneFromState(stop.address.state) : undefined);
    return toStopDateTimeObject(value, stopTz);
  }

  startMilestoneEdit(stopNumber: number | null | undefined, milestone: string, currentValue: any): void {
    if (!stopNumber) return;
    this.editingMilestone.set({ stopNumber, milestone });
    if (milestone === 'dockNumber') {
      this.milestoneDockControl.setValue(currentValue || '');
    } else {
      const stop = this.load.stops.find((s) => s.stopNumber === stopNumber);
      this.milestoneDateTimeControl.setValue(this.toDateTimeObject(currentValue, stop));
    }
  }

  cancelMilestoneEdit(): void {
    this.editingMilestone.set(null);
  }

  isEditingMilestone(stopNumber: number | null | undefined, milestone: string): boolean {
    const current = this.editingMilestone();
    return !!current && current.stopNumber === stopNumber && current.milestone === milestone;
  }

  async saveMilestoneEdit(stopNumber: number | null | undefined, milestone: string): Promise<void> {
    if (!stopNumber) return;
    this.isSavingMilestone.set(true);
    try {
      const updatedStops = [...this.load.stops];
      const stopIndex = updatedStops.findIndex((s) => s.stopNumber === stopNumber);
      if (stopIndex === -1) return;

      const stop = { ...updatedStops[stopIndex] };

      if (milestone === 'dockNumber') {
        stop.dockNumber = this.milestoneDockControl.value ? this.milestoneDockControl.value.trim() : null;
      } else {
        const dt = this.milestoneDateTimeControl.value;
        if (!dt?.date || !dt?.time) {
          this.toastService.show('error', 'Please select both date and time');
          return;
        }
        const stopTz = stop.timezone || guessTimezoneFromState(stop.address?.state);
        const parsedDate = dateInTimezone(dt.date, dt.time, stopTz);
        if (isNaN(parsedDate.getTime())) {
          this.toastService.show('error', 'Invalid date/time format');
          return;
        }
        const ts = Timestamp.fromDate(parsedDate);

        if (milestone === 'etaDateTime') {
          stop.etaDateTime = dt;
        } else if (milestone === 'arrivedAt') {
          stop.arrivedAt = ts;
          stop.arrivalDateTime = dt;
        } else if (milestone === 'checkedInAt') {
          stop.checkedInAt = ts;
          stop.checkInDateTime = dt;
        } else if (milestone === 'dockedAt') {
          stop.dockedAt = ts;
        } else if (milestone === 'loadingStartedAt') {
          stop.loadingStartedAt = ts;
        } else if (milestone === 'loadedAt') {
          stop.loadedAt = ts;
        } else if (milestone === 'checkedOutAt') {
          stop.checkedOutAt = ts;
          stop.checkOutDateTime = dt;
        } else if (milestone === 'departedAt') {
          stop.departedAt = ts;
          stop.departureDateTime = dt;
        }
      }

      updatedStops[stopIndex] = stop;
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));

      // Update local stopForms signal
      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop } : f))
      );

      this.toastService.show('success', 'Milestone updated successfully');
      this.editingMilestone.set(null);
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', err?.message || 'Failed to update milestone');
    } finally {
      this.isSavingMilestone.set(false);
    }
  }

  toggleDocUpload(stopNumber: number | null | undefined): void {
    if (!stopNumber) return;
    if (this.showingDocUpload() === stopNumber) {
      this.showingDocUpload.set(null);
    } else {
      this.showingDocUpload.set(stopNumber);
    }
  }

  getDocumentLabel(docKey: string): string {
    const labels: Record<string, string> = {
      billOfLading: 'Bill of Lading',
      bol: 'Bill of Lading',
      loadImage: 'Load Photo',
      sealImage: 'Seal Photo',
      temperatureImage: 'Temperature Reading',
      proofOfDelivery: 'Proof of Delivery',
      pod: 'Proof of Delivery',
      lumperReceipt: 'Lumper Receipt',
    };
    return labels[docKey] || 'Document';
  }

  async autoSaveDocument(
    stopNumber: number | null | undefined,
    docKey: string,
    url: string | null
  ): Promise<void> {
    if (!stopNumber) return;
    const stopIndex = this.load.stops.findIndex((s) => s.stopNumber === stopNumber);
    if (stopIndex === -1) return;

    const stop = this.load.stops[stopIndex];
    const currentDoc = (stop.documents as any)?.[docKey];
    if (currentDoc?.url === url && currentDoc?.status !== 'rejected') {
      return;
    }

    const newDocs: any = { ...(stop.documents || {}) };
    if (url) {
      newDocs[docKey] = {
        ...(currentDoc || {}),
        url,
        version: (currentDoc?.version || 0) + 1,
        createdAt: currentDoc?.createdAt || new Date(),
        status: url === 'N/A' ? null : (currentDoc?.status === 'rejected' ? 'pending' : (currentDoc?.status || 'pending')),
        rejectionReason: currentDoc?.status === 'rejected' ? null : currentDoc?.rejectionReason,
      };
    } else {
      delete newDocs[docKey];
    }

    const updatedStops = [...this.load.stops];
    const updatedStop: Stop = { ...stop, documents: newDocs };
    updatedStops[stopIndex] = updatedStop;
    this.load.stops = updatedStops;

    try {
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));

      if (docKey === 'lumperReceipt') {
        const companyId = (this.load as any).view?.companyId || this.load.carrierId || '';
        await this.loadService.syncStopLumpers(this.load.id, updatedStops, companyId, this.load.driver1Id);

        const fee = Number(stop.lumperFee) || 0;
        if (url && url !== 'N/A' && fee <= 0) {
          this.toastService.show('warning', 'Please enter a lumper fee greater than $0 for this receipt.', {
            title: 'Fee Required',
            duration: 4000,
          });
        }
      }

      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop: updatedStop } : f))
      );
      this.replacingDocs.update((current) => ({
        ...current,
        [`${stopNumber}_${docKey}`]: false,
      }));
      this.toastService.show('success', `${this.getDocumentLabel(docKey)} saved`);
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', `Failed to save ${this.getDocumentLabel(docKey)}: ${err?.message}`);
    }
  }

  isDraggingOver(stopNumber: number | null | undefined, docKey: string): boolean {
    if (!stopNumber) return false;
    return !!this.draggingOverDoc()[`${stopNumber}_${docKey}`];
  }

  onDocDragOver(event: DragEvent, stopNumber: number | null | undefined, docKey: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (!stopNumber) return;
    this.draggingOverDoc.update((m) => ({ ...m, [`${stopNumber}_${docKey}`]: true }));
  }

  onDocDragLeave(event: DragEvent, stopNumber: number | null | undefined, docKey: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (!stopNumber) return;
    this.draggingOverDoc.update((m) => ({ ...m, [`${stopNumber}_${docKey}`]: false }));
  }

  async onDocDrop(
    event: DragEvent,
    stopNumber: number | null | undefined,
    docKey: string,
    formControlName: string,
    subfolder: 'documents' | 'images' = 'documents'
  ): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (!stopNumber) return;
    this.draggingOverDoc.update((m) => ({ ...m, [`${stopNumber}_${docKey}`]: false }));

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!validExtensions.includes(ext)) {
      this.toastService.show('warning', 'Please upload a PDF, PNG, or JPG file.', { title: 'Invalid File Type' });
      return;
    }

    await this.uploadFileDirectly(file, stopNumber, docKey, formControlName, subfolder);
  }

  async handleDirectDocUpload(
    event: Event,
    stopNumber: number | null | undefined,
    docKey: string,
    formControlName: string,
    subfolder: 'documents' | 'images' = 'documents'
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !stopNumber) return;

    await this.uploadFileDirectly(file, stopNumber, docKey, formControlName, subfolder);
    input.value = '';
  }

  async uploadFileDirectly(
    file: File,
    stopNumber: number,
    docKey: string,
    formControlName: string,
    subfolder: 'documents' | 'images' = 'documents'
  ): Promise<void> {
    const path = `loads/${this.load.id}/stops/${stopNumber}/${subfolder}`;
    this.uploadingDoc.set({ stopNumber, docKey, progress: 0 });

    this.storageService.uploadFile(file, path).subscribe({
      next: async (upload) => {
        this.uploadingDoc.set({ stopNumber, docKey, progress: Math.round(upload.progress) });
        if (upload.downloadUrl) {
          const stopForm = this.stopForms().find((sf) => sf.stop.stopNumber === stopNumber);
          if (stopForm) {
            stopForm.form.get(formControlName)?.setValue(upload.downloadUrl, { emitEvent: false });
          }
          await this.autoSaveDocument(stopNumber, docKey, upload.downloadUrl);
          this.uploadingDoc.set(null);
        }
      },
      error: (err) => {
        this.toastService.show('error', `Failed to upload ${this.getDocumentLabel(docKey)}: ${err?.message || err}`);
        this.uploadingDoc.set(null);
      },
    });
  }

  async saveDocuments(stopNumber: number | null | undefined): Promise<void> {
    if (!stopNumber) return;
    const stopForm = this.stopForms().find((sf) => sf.stop.stopNumber === stopNumber);
    if (!stopForm) return;

    this.isSavingDocuments.set(true);
    try {
      const formValue = stopForm.form.getRawValue();
      const updatedStops = [...this.load.stops];
      const stopIndex = updatedStops.findIndex((s) => s.stopNumber === stopNumber);
      if (stopIndex === -1) return;

      const stop = { ...updatedStops[stopIndex] };
      const currentTime = Date.now();
      const newDocs: any = { ...(stop.documents || {}) };

      if (stop.stopType === 'pickup' || stop.stopType === 'both') {
        if (formValue.bol || formValue.noBol) {
          const prev = stop.documents?.billOfLading;
          newDocs.billOfLading = {
            url: formValue.noBol ? 'N/A' : formValue.bol,
            version: (prev?.version || 0) + 1,
            createdAt: prev?.createdAt || new Date(currentTime),
            status: formValue.noBol ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
            rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
          };
        }
        if (formValue.loadImage || formValue.noLoadImage) {
          const prev = stop.documents?.loadImage;
          newDocs.loadImage = {
            url: formValue.noLoadImage ? 'N/A' : formValue.loadImage,
            version: (prev?.version || 0) + 1,
            createdAt: prev?.createdAt || new Date(currentTime),
            status: formValue.noLoadImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
            rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
          };
        }
        if (formValue.sealImage || formValue.noSealImage) {
          const prev = stop.documents?.sealImage;
          newDocs.sealImage = {
            url: formValue.noSealImage ? 'N/A' : formValue.sealImage,
            version: (prev?.version || 0) + 1,
            createdAt: prev?.createdAt || new Date(currentTime),
            status: formValue.noSealImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
            rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
          };
        }
        if (this.isReeferLoad() && (formValue.temperatureImage || formValue.noTemperatureImage)) {
          const prev = stop.documents?.temperatureImage;
          newDocs.temperatureImage = {
            url: formValue.noTemperatureImage ? 'N/A' : formValue.temperatureImage,
            version: (prev?.version || 0) + 1,
            createdAt: prev?.createdAt || new Date(currentTime),
            status: formValue.noTemperatureImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
            rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
          };
        }
      }

      if (stop.stopType === 'delivery' || stop.stopType === 'both') {
        if (formValue.pod || formValue.noPod) {
          const prev = stop.documents?.proofOfDelivery;
          newDocs.proofOfDelivery = {
            url: formValue.noPod ? 'N/A' : formValue.pod,
            version: (prev?.version || 0) + 1,
            createdAt: prev?.createdAt || new Date(currentTime),
            status: formValue.noPod ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
            rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
          };
        }
      }

      if (formValue.lumperReceipt || formValue.noLumperReceipt) {
        const prev = stop.documents?.lumperReceipt;
        newDocs.lumperReceipt = {
          url: formValue.noLumperReceipt ? 'N/A' : formValue.lumperReceipt,
          version: (prev?.version || 0) + 1,
          createdAt: prev?.createdAt || new Date(currentTime),
          status: formValue.noLumperReceipt ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
          rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
        };
      }

      stop.documents = newDocs;
      updatedStops[stopIndex] = stop;
      this.load.stops = updatedStops;
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));

      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop } : f))
      );

      this.toastService.show('success', 'Documents updated successfully');
      this.showingDocUpload.set(null);
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', err?.message || 'Failed to save documents');
    } finally {
      this.isSavingDocuments.set(false);
    }
  }

  toggleCustomActionTime(stopNumber: number | null | undefined): void {
    if (!stopNumber) return;
    this.showCustomActionTime.update((current) => ({
      ...current,
      [stopNumber]: !current[stopNumber],
    }));
  }

  getStopDocuments(stop: Stop): Array<{
    key: string;
    docKey: string;
    label: string;
    icon: string;
    emoji: string;
    url?: string | null;
    isNA?: boolean;
    hasDoc: boolean;
    status?: 'pending' | 'approved' | 'rejected' | null;
    rejectionReason?: string | null;
    reviewedBy?: string | null;
    reviewedAt?: any | null;
  }> {
    const isPickup = stop.stopType === 'pickup' || stop.stopType === 'both';
    const isDelivery = stop.stopType === 'delivery' || stop.stopType === 'both';
    const docs = stop.documents || {};

    const list: Array<{
      key: string;
      docKey: string;
      label: string;
      icon: string;
      emoji: string;
      url?: string | null;
      isNA?: boolean;
      hasDoc: boolean;
      status?: 'pending' | 'approved' | 'rejected' | null;
      rejectionReason?: string | null;
      reviewedBy?: string | null;
      reviewedAt?: any | null;
    }> = [];

    if (isPickup) {
      const bolDoc = docs.billOfLading as any;
      const bolUrl = this.extractStopDocUrl(bolDoc);
      const isNA = bolUrl === 'N/A';
      const hasDoc = !!bolUrl && !isNA;
      list.push({
        key: 'bol',
        docKey: 'billOfLading',
        label: 'Bill of Lading',
        icon: 'FileText',
        emoji: '📋',
        url: isNA ? null : bolUrl,
        isNA,
        hasDoc,
        status: isNA ? null : (bolDoc?.status || (hasDoc ? 'pending' : null)),
        rejectionReason: bolDoc?.rejectionReason,
        reviewedBy: bolDoc?.reviewedBy,
        reviewedAt: bolDoc?.reviewedAt,
      });

      const loadDoc = docs.loadImage as any;
      const loadUrl = this.extractStopDocUrl(loadDoc);
      const loadNA = loadUrl === 'N/A';
      const loadHas = !!loadUrl && !loadNA;
      list.push({
        key: 'loadImage',
        docKey: 'loadImage',
        label: 'Load Photo',
        icon: 'Image',
        emoji: '📦',
        url: loadNA ? null : loadUrl,
        isNA: loadNA,
        hasDoc: loadHas,
        status: loadNA ? null : (loadDoc?.status || (loadHas ? 'pending' : null)),
        rejectionReason: loadDoc?.rejectionReason,
        reviewedBy: loadDoc?.reviewedBy,
        reviewedAt: loadDoc?.reviewedAt,
      });

      const sealDoc = docs.sealImage as any;
      const sealUrl = this.extractStopDocUrl(sealDoc);
      const sealNA = sealUrl === 'N/A';
      const sealHas = !!sealUrl && !sealNA;
      list.push({
        key: 'sealImage',
        docKey: 'sealImage',
        label: 'Seal Photo',
        icon: 'Image',
        emoji: '🔒',
        url: sealNA ? null : sealUrl,
        isNA: sealNA,
        hasDoc: sealHas,
        status: sealNA ? null : (sealDoc?.status || (sealHas ? 'pending' : null)),
        rejectionReason: sealDoc?.rejectionReason,
        reviewedBy: sealDoc?.reviewedBy,
        reviewedAt: sealDoc?.reviewedAt,
      });

      if (this.isReeferLoad()) {
        const tempDoc = docs.temperatureImage as any;
        const tempUrl = this.extractStopDocUrl(tempDoc);
        const tempNA = tempUrl === 'N/A';
        const tempHas = !!tempUrl && !tempNA;
        list.push({
          key: 'temperatureImage',
          docKey: 'temperatureImage',
          label: 'Temperature Reading',
          icon: 'Thermometer',
          emoji: '❄️',
          url: tempNA ? null : tempUrl,
          isNA: tempNA,
          hasDoc: tempHas,
          status: tempNA ? null : (tempDoc?.status || (tempHas ? 'pending' : null)),
          rejectionReason: tempDoc?.rejectionReason,
          reviewedBy: tempDoc?.reviewedBy,
          reviewedAt: tempDoc?.reviewedAt,
        });
      }
    }

    if (isDelivery) {
      const podDoc = docs.proofOfDelivery as any;
      const podUrl = this.extractStopDocUrl(podDoc);
      const isNA = podUrl === 'N/A';
      const hasDoc = !!podUrl && !isNA;
      list.push({
        key: 'pod',
        docKey: 'proofOfDelivery',
        label: 'Proof of Delivery',
        icon: 'FileCheck',
        emoji: '✍️',
        url: isNA ? null : podUrl,
        isNA,
        hasDoc,
        status: isNA ? null : (podDoc?.status || (hasDoc ? 'pending' : null)),
        rejectionReason: podDoc?.rejectionReason,
        reviewedBy: podDoc?.reviewedBy,
        reviewedAt: podDoc?.reviewedAt,
      });
    }

    const lumperDoc = docs.lumperReceipt as any;
    const lumperUrl = this.extractStopDocUrl(lumperDoc);
    const lumperNA = lumperUrl === 'N/A';
    const lumperHas = !!lumperUrl && !lumperNA;
    if (lumperHas) {
      list.push({
        key: 'lumperReceipt',
        docKey: 'lumperReceipt',
        label: 'Lumper Receipt',
        icon: 'Receipt',
        emoji: '🧾',
        url: lumperUrl,
        isNA: false,
        hasDoc: true,
        status: lumperDoc?.status || 'pending',
        rejectionReason: lumperDoc?.rejectionReason,
        reviewedBy: lumperDoc?.reviewedBy,
        reviewedAt: lumperDoc?.reviewedAt,
      });
    }

    return list;
  }

  // Document Validation & Gating helpers
  areAllRequiredDocumentsSubmitted(stop: Stop): boolean {
    const isPickup = stop.stopType === 'pickup' || stop.stopType === 'both';
    const isDelivery = stop.stopType === 'delivery' || stop.stopType === 'both';
    const docs = stop.documents || {};

    if (isPickup) {
      if (!this.extractStopDocUrl(docs.billOfLading)) return false;
      if (!this.extractStopDocUrl(docs.loadImage)) return false;
      if (!this.extractStopDocUrl(docs.sealImage)) return false;
      if (this.isReeferLoad() && !this.extractStopDocUrl(docs.temperatureImage)) return false;
    }

    if (isDelivery) {
      if (!this.extractStopDocUrl(docs.proofOfDelivery)) return false;
    }

    return true;
  }

  getMissingRequiredDocuments(stop: Stop): string[] {
    const isPickup = stop.stopType === 'pickup' || stop.stopType === 'both';
    const isDelivery = stop.stopType === 'delivery' || stop.stopType === 'both';
    const docs = stop.documents || {};
    const missing: string[] = [];

    if (isPickup) {
      if (!this.extractStopDocUrl(docs.billOfLading)) missing.push('Bill of Lading');
      if (!this.extractStopDocUrl(docs.loadImage)) missing.push('Load Photo');
      if (!this.extractStopDocUrl(docs.sealImage)) missing.push('Seal Photo');
      if (this.isReeferLoad() && !this.extractStopDocUrl(docs.temperatureImage)) missing.push('Temperature Reading');
    }

    if (isDelivery) {
      if (!this.extractStopDocUrl(docs.proofOfDelivery)) missing.push('Proof of Delivery');
    }

    return missing;
  }

  getRejectedDocuments(stop: Stop): Array<{ docKey: string; label: string; reason?: string | null }> {
    const docs = this.getStopDocuments(stop);
    return docs
      .filter((d) => d.hasDoc && d.status === 'rejected')
      .map((d) => ({ docKey: d.docKey, label: d.label, reason: d.rejectionReason }));
  }

  hasAnyRejectedDocument(stop: Stop): boolean {
    const docs = this.getStopDocuments(stop);
    return docs.some((d) => d.hasDoc && d.status === 'rejected');
  }

  private extractStopDocUrl(raw: any): string | null {
    if (!raw) return null;
    if (typeof raw === 'string') return raw.trim() || null;
    if (Array.isArray(raw)) {
      const latest = raw[raw.length - 1];
      return this.extractStopDocUrl(latest);
    }
    if (typeof raw === 'object' && typeof raw.url === 'string') {
      return raw.url.trim() || null;
    }
    return null;
  }

  getDocPreviewUrl(stopForm: StopFormData, docKey: string, formControlName: string): string | null {
    const formVal = stopForm.form.get(formControlName)?.value;
    if (formVal && typeof formVal === 'string' && formVal !== 'N/A' && formVal.trim() !== '') {
      return formVal;
    }
    const stopDoc = (stopForm.stop.documents as any)?.[docKey];
    const extracted = this.extractStopDocUrl(stopDoc);
    if (extracted && extracted !== 'N/A') {
      return extracted;
    }
    return null;
  }

  getDocumentApprovalStats(stop: Stop): {
    total: number;
    approved: number;
    rejected: number;
    pending: number;
    missing: number;
    approvedPercent: number;
    rejectedPercent: number;
    pendingPercent: number;
  } {
    const docs = this.getStopDocuments(stop);
    const total = docs.length;
    let approved = 0;
    let rejected = 0;
    let pending = 0;
    let missing = 0;

    for (const d of docs) {
      if (d.isNA) {
        approved++;
      } else if (!d.hasDoc) {
        missing++;
      } else if (d.status === 'approved') {
        approved++;
      } else if (d.status === 'rejected') {
        rejected++;
      } else {
        pending++;
      }
    }

    const approvedPercent = total > 0 ? Math.round((approved / total) * 100) : 0;
    const rejectedPercent = total > 0 ? Math.round((rejected / total) * 100) : 0;
    const pendingPercent = total > 0 ? Math.round((pending / total) * 100) : 0;

    return {
      total,
      approved,
      rejected,
      pending,
      missing,
      approvedPercent,
      rejectedPercent,
      pendingPercent,
    };
  }

  areAllDocumentsApproved(stop: Stop): boolean {
    const docs = this.getStopDocuments(stop);
    if (!this.areAllRequiredDocumentsSubmitted(stop)) return false;
    return docs.every((d) => d.isNA || d.status === 'approved');
  }

  isActionDisabled(stop: Stop, action: string): boolean {
    if (this.actionLoading()) return true;
    if (action === 'CHECK_OUT') {
      return !this.areAllDocumentsApproved(stop);
    }
    if (action === 'WAIT_APPROVAL') {
      return !this.areAllRequiredDocumentsSubmitted(stop) || this.hasAnyRejectedDocument(stop);
    }
    return false;
  }

  // Single Document Approval & Rejection
  async approveSingleDocument(
    stopNumber: number | null | undefined,
    docKey: string
  ): Promise<void> {
    if (!stopNumber) return;
    const stopIndex = this.load.stops.findIndex((s) => s.stopNumber === stopNumber);
    if (stopIndex === -1) return;

    const stop = this.load.stops[stopIndex];
    const currentDoc = (stop.documents as any)?.[docKey];
    if (!currentDoc || !currentDoc.url) {
      this.toastService.show('error', 'No document found to approve');
      return;
    }

    const updatedDoc = {
      ...currentDoc,
      status: 'approved',
      rejectionReason: null,
      reviewedAt: new Date(),
    };

    const newDocs = { ...(stop.documents || {}), [docKey]: updatedDoc };
    let newStatus = stop.status;
    let newApprovalStatus = stop.approvalStatus;

    const tempStop = { ...stop, documents: newDocs };
    const allApproved = this.areAllDocumentsApproved(tempStop);
    if (allApproved && (stop.status === 'WAITING_APPROVAL' || stop.status === 'REJECT_DOCS')) {
      newStatus = 'APPROVED';
      newApprovalStatus = 'approved';
    }

    const updatedStop: Stop = {
      ...stop,
      documents: newDocs,
      status: newStatus,
      approvalStatus: newApprovalStatus,
      ...(allApproved ? { rejectionNotes: null } : {}),
    };

    const updatedStops = [...this.load.stops];
    updatedStops[stopIndex] = updatedStop;
    this.load.stops = updatedStops;

    try {
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));
      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop: updatedStop } : f))
      );
      this.toastService.show('success', `${this.getDocumentLabel(docKey)} approved`);
      if (allApproved && stop.status === 'WAITING_APPROVAL') {
        await this.executeStopAction(stopNumber, 'APPROVE', undefined, false);
      }
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', `Failed to approve ${this.getDocumentLabel(docKey)}: ${err?.message}`);
    }
  }

  async approveAllDocuments(stopNumber: number | null | undefined): Promise<void> {
    if (!stopNumber) return;
    const stopIndex = this.load.stops.findIndex((s) => s.stopNumber === stopNumber);
    if (stopIndex === -1) return;

    const stop = this.load.stops[stopIndex];
    if (!this.areAllRequiredDocumentsSubmitted(stop)) {
      this.toastService.show('error', 'Please ensure all required documents are uploaded or marked N/A first.');
      return;
    }

    const docs = stop.documents || {};
    const newDocs: any = { ...docs };
    const reviewedAt = new Date();

    for (const key of Object.keys(newDocs)) {
      const doc = newDocs[key];
      if (doc && doc.url && doc.url !== 'N/A') {
        newDocs[key] = {
          ...doc,
          status: 'approved',
          rejectionReason: null,
          reviewedAt,
        };
      }
    }

    const updatedStop: Stop = {
      ...stop,
      documents: newDocs,
      status: 'APPROVED',
      approvalStatus: 'approved',
      rejectionNotes: null,
    };

    const updatedStops = [...this.load.stops];
    updatedStops[stopIndex] = updatedStop;
    this.load.stops = updatedStops;

    try {
      this.actionLoading.set({ stopNumber, action: 'APPROVE' });
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));
      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop: updatedStop } : f))
      );
      this.toastService.show('success', 'All documents approved');
      await this.executeStopAction(stopNumber, 'APPROVE', undefined, false);
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', `Failed to approve all documents: ${err?.message}`);
    } finally {
      this.actionLoading.set(null);
    }
  }

  startDocRejection(stopNumber: number | null | undefined, docKey: string): void {
    if (!stopNumber) return;
    this.rejectingDoc.set({ stopNumber, docKey });
    this.docRejectionReason.set('');
  }

  cancelDocRejection(): void {
    this.rejectingDoc.set(null);
    this.docRejectionReason.set('');
  }

  async confirmDocRejection(stopNumber: number | null | undefined, docKey: string): Promise<void> {
    if (!stopNumber) return;
    const reason = this.docRejectionReason().trim();
    if (!reason) {
      this.toastService.show('error', 'Please provide a reason for rejecting the document');
      return;
    }

    const stopIndex = this.load.stops.findIndex((s) => s.stopNumber === stopNumber);
    if (stopIndex === -1) return;

    const stop = this.load.stops[stopIndex];
    const currentDoc = (stop.documents as any)?.[docKey];
    if (!currentDoc || !currentDoc.url) {
      this.toastService.show('error', 'No document found to reject');
      return;
    }

    const updatedDoc = {
      ...currentDoc,
      status: 'rejected',
      rejectionReason: reason,
      reviewedAt: new Date(),
    };

    const newDocs = { ...(stop.documents || {}), [docKey]: updatedDoc };
    const label = this.getDocumentLabel(docKey);
    const overallRejectionNote = `${label}: ${reason}`;

    const updatedStop: Stop = {
      ...stop,
      documents: newDocs,
      status: 'REJECT_DOCS',
      approvalStatus: 'rejected',
      rejectionNotes: overallRejectionNote,
    };

    const updatedStops = [...this.load.stops];
    updatedStops[stopIndex] = updatedStop;
    this.load.stops = updatedStops;

    this.rejectingDoc.set(null);
    this.docRejectionReason.set('');

    try {
      await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));
      this.stopForms.update((forms) =>
        forms.map((f) => (f.stop.stopNumber === stopNumber ? { ...f, stop: updatedStop } : f))
      );
      this.toastService.show('success', `${label} marked as rejected`);
      // Also notify backend cloud function to synchronize status
      await this.executeStopAction(stopNumber, 'REJECT_DOCS', overallRejectionNote, false);
    } catch (err: any) {
      this.toastService.show('error', `Failed to reject ${label}: ${err?.message}`);
    }
  }

  // Map current stop status to the next available stop action(s)
  getNextActions(stop: Stop): { action: string; label: string; icon: string }[] {
    const isRelayStop = !!(stop.handoffToSegmentId || stop.stopType === 'handoff');
    const isYardOrRelayOnly =
      stop.stopType === 'handoff' ||
      stop.stopType === 'yard_drop' ||
      stop.stopType === 'yard_hook' ||
      stop.stopType === 'custom';

    if (isRelayStop && stop.status && stop.status !== 'PENDING' && stop.status !== 'DEPARTED') {
      const relayActions: { action: string; label: string; icon: string }[] = [];
      if (stop.status === 'EN_ROUTE') {
        relayActions.push({ action: 'ARRIVE', label: 'Mark Gate / Yard Arrival', icon: 'MapPinCheck' });
      }
      if (stop.relay?.dropStatus !== 'COMPLETED') {
        relayActions.push({
          action: 'RELAY_DROP_COMPLETE',
          label: 'Confirm Outgoing Trailer Drop',
          icon: 'PackageCheck',
        });
      } else if (stop.relay?.hookStatus !== 'COMPLETED') {
        relayActions.push({
          action: 'RELAY_HOOK_COMPLETE',
          label: 'Confirm Oncoming Hook & Depart',
          icon: 'Link',
        });
      }
      if (!isYardOrRelayOnly && stop.status !== 'EN_ROUTE' && stop.status !== 'CHECKED_OUT') {
        relayActions.push({ action: 'CHECK_OUT', label: 'Check Out Commercial Dock', icon: 'LogOut' });
      }
      if (relayActions.length > 0) {
        return relayActions;
      }
    }

    if (isYardOrRelayOnly && stop.status === 'ARRIVED') {
      return [{ action: 'DEPART', label: 'Mark Departed', icon: 'ArrowRightFromLine' }];
    }

    const statusToActions: Record<string, { action: string; label: string; icon: string }[]> = {
      'PENDING': [], // Handled by dedicated Provide ETA form in template
      'EN_ROUTE': [
        { action: 'ARRIVE', label: 'Mark Gate Arrival', icon: 'MapPinCheck' },
        { action: 'CHECK_IN', label: 'Check In at Window', icon: 'LogIn' },
      ],
      'ARRIVED': [
        { action: 'CHECK_IN', label: 'Check In at Window', icon: 'LogIn' },
      ],
      'CHECKED_IN': [
        { action: 'DOCK', label: 'Mark Docked', icon: 'Anchor' },
        { action: 'CHECK_OUT', label: 'Check Out (No Dock)', icon: 'LogOut' },
      ],
      'DOCKED': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'LOADING': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'UNLOADING': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'LOADED': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'UNLOADED': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'WAITING_APPROVAL': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'APPROVED': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'REJECT_DOCS': [
        { action: 'CHECK_OUT', label: 'Check Out & Submit Paperwork', icon: 'LogOut' },
      ],
      'CHECKED_OUT': [
        { action: 'DEPART', label: 'Mark Departed', icon: 'ArrowRightFromLine' },
      ],
      'DEPARTED': [], // Terminal
    };
    return statusToActions[stop.status || 'PENDING'] || [];
  }

  isSkippableAction(action: string): boolean {
    const nonSkippable = ['CHECK_IN', 'CHECK_OUT', 'APPROVE', 'REJECT_DOCS', 'WAIT_APPROVAL'];
    return !nonSkippable.includes(action);
  }

  async skipAction(stopNumber: number | null | undefined, action: string): Promise<void> {
    if (!stopNumber) return;
    const skipTransitions: Record<string, StopStatusType> = {
      'ARRIVE': 'ARRIVED',
      'DOCK': 'DOCKED',
      'START_LOADING': 'LOADING',
      'START_UNLOADING': 'UNLOADING',
      'DEPART': 'DEPARTED',
    };
    const targetStatus = skipTransitions[action];
    if (!targetStatus) return;

    const stopIndex = this.load.stops.findIndex(s => s.stopNumber === stopNumber);
    if (stopIndex === -1) return;

    this.actionLoading.set({ stopNumber, action: `SKIP_${action}` });
    try {
      const updatedStops = [...this.load.stops];
      const stop = { ...updatedStops[stopIndex], status: targetStatus };
      updatedStops[stopIndex] = stop;
      this.load.stops = updatedStops;

      this.stopForms.update(forms =>
        forms.map(f => (f.stop.stopNumber === stopNumber ? { ...f, stop } : f))
      );

      const allDeparted =
        updatedStops.length > 0 && updatedStops.every((s) => s.status === 'DEPARTED');
      if (allDeparted) {
        this.load.status = 'DELIVERED';
      }

      await firstValueFrom(
        this.loadService.updateLoad(this.load.id, {
          stops: updatedStops,
          ...(allDeparted ? { status: 'DELIVERED' } : {}),
        })
      );
      this.toastService.show('info', `Stop ${stopNumber}: ${action} skipped`);
      this.onSuccess?.();
    } catch (err: any) {
      this.toastService.show('error', `Failed to skip action: ${err?.message || err}`);
    } finally {
      this.actionLoading.set(null);
    }
  }

  handleActionClick(stopNumber: number | null | undefined, action: string): void {
    if (!stopNumber) return;
    const stop = this.load.stops.find((s) => s.stopNumber === stopNumber);
    if (stop && this.isActionDisabled(stop, action)) {
      if (action === 'CHECK_OUT') {
        this.toastService.show('error', 'All required documents must be uploaded and approved before checkout.');
      }
      return;
    }

    if (action === 'REJECT_DOCS') {
      this.rejectingStopNumber.set(stopNumber);
      this.rejectionReason.set('');
      return;
    }
    this.executeStopAction(stopNumber, action);
  }

  confirmRejectDocs(stopNumber: number | null | undefined): void {
    const reason = this.rejectionReason().trim();
    this.executeStopAction(stopNumber, 'REJECT_DOCS', reason || undefined);
    this.rejectingStopNumber.set(null);
    this.rejectionReason.set('');
  }

  async executeStopAction(
    stopNumber: number | null | undefined,
    action: string,
    reason?: string,
    closeOnComplete: boolean = true
  ): Promise<void> {
    if (!stopNumber) {
      this.toastService.show('error', 'Invalid stop number');
      return;
    }

    let customTime: DateTimeObject | null = null;
    if (action !== 'APPROVE' && action !== 'REJECT_DOCS') {
      const timeVal = this.actionDateTimeControls[stopNumber]?.value;
      if (!timeVal || !timeVal.date || !timeVal.time) {
        this.toastService.show('warning', 'Please specify both date and time for this milestone.', {
          title: 'Date & Time Required',
        });
        return;
      }
      customTime = timeVal;
    }

    this.actionLoading.set({ stopNumber, action });
    try {
      const stopForm = this.stopForms().find(sf => sf.stop.stopNumber === stopNumber);
      if (stopForm) {
        const formValue = stopForm.form.getRawValue();
        const updatedStops = [...this.load.stops];
        const stopIndex = updatedStops.findIndex(s => s.stopNumber === stopNumber);
        const stop = { ...updatedStops[stopIndex] };
        
        let needsUpdate = false;
        
        if (action === 'DOCK') {
          stop.dockNumber = formValue.noDockNumber ? 'N/A' : (formValue.dockNumber || null);
          needsUpdate = true;
        }
        
        const currentTime = Date.now();
        if (['CHECK_OUT', 'START_LOADING', 'LOADED', 'START_UNLOADING', 'UNLOADED', 'WAIT_APPROVAL'].includes(action)) {
          if (stop.stopType === 'pickup' || stop.stopType === 'both') {
            const newDocs: any = { ...(stop.documents || {}) };
            if (formValue.bol || formValue.noBol) {
              const prev = stop.documents?.billOfLading;
              newDocs.billOfLading = {
                url: formValue.noBol ? 'N/A' : formValue.bol,
                version: (prev?.version || 0) + 1,
                createdAt: prev?.createdAt || new Date(currentTime),
                status: formValue.noBol ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
                rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
              };
            }
            if (formValue.loadImage || formValue.noLoadImage) {
              const prev = stop.documents?.loadImage;
              newDocs.loadImage = {
                url: formValue.noLoadImage ? 'N/A' : formValue.loadImage,
                version: (prev?.version || 0) + 1,
                createdAt: prev?.createdAt || new Date(currentTime),
                status: formValue.noLoadImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
                rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
              };
            }
            if (formValue.sealImage || formValue.noSealImage) {
              const prev = stop.documents?.sealImage;
              newDocs.sealImage = {
                url: formValue.noSealImage ? 'N/A' : formValue.sealImage,
                version: (prev?.version || 0) + 1,
                createdAt: prev?.createdAt || new Date(currentTime),
                status: formValue.noSealImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
                rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
              };
            }
            if (this.isReeferLoad() && (formValue.temperatureImage || formValue.noTemperatureImage)) {
              const prev = stop.documents?.temperatureImage;
              newDocs.temperatureImage = {
                url: formValue.noTemperatureImage ? 'N/A' : formValue.temperatureImage,
                version: (prev?.version || 0) + 1,
                createdAt: prev?.createdAt || new Date(currentTime),
                status: formValue.noTemperatureImage ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
                rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
              };
            }
            if (Object.keys(newDocs).length > 0) {
              stop.documents = newDocs;
              needsUpdate = true;
            }
          }
          if (stop.stopType === 'delivery' || stop.stopType === 'both') {
            const newDocs: any = { ...(stop.documents || {}) };
            if (formValue.pod || formValue.noPod) {
              const prev = stop.documents?.proofOfDelivery;
              newDocs.proofOfDelivery = {
                url: formValue.noPod ? 'N/A' : formValue.pod,
                version: (prev?.version || 0) + 1,
                createdAt: prev?.createdAt || new Date(currentTime),
                status: formValue.noPod ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
                rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
              };
              stop.documents = newDocs;
              needsUpdate = true;
            }
          }
          if (formValue.lumperReceipt || formValue.noLumperReceipt) {
            const newDocs: any = { ...(stop.documents || {}) };
            const prev = stop.documents?.lumperReceipt;
            newDocs.lumperReceipt = {
              url: formValue.noLumperReceipt ? 'N/A' : formValue.lumperReceipt,
              version: (prev?.version || 0) + 1,
              createdAt: prev?.createdAt || new Date(currentTime),
              status: formValue.noLumperReceipt ? null : (prev?.status === 'rejected' ? 'pending' : (prev?.status || 'pending')),
              rejectionReason: prev?.status === 'rejected' ? null : prev?.rejectionReason,
            };
            stop.documents = newDocs;
            needsUpdate = true;
          }
        }

        // On overall APPROVE, mark all submitted documents as approved
        if (action === 'APPROVE') {
          const docs: any = { ...(stop.documents || {}) };
          let docChanged = false;
          for (const key of ['billOfLading', 'loadImage', 'sealImage', 'temperatureImage', 'proofOfDelivery', 'lumperReceipt'] as const) {
            if (docs[key]?.url && docs[key]?.url !== 'N/A' && docs[key]?.status !== 'approved') {
              docs[key] = {
                ...docs[key],
                status: 'approved',
                reviewedAt: new Date(),
              };
              docChanged = true;
            }
          }
          if (docChanged) {
            stop.documents = docs;
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
           updatedStops[stopIndex] = stop;
           this.load.stops = updatedStops;
           await firstValueFrom(this.loadService.updateLoad(this.load.id, { stops: updatedStops }));
           this.stopForms.update(forms =>
             forms.map(f => (f.stop.stopNumber === stopNumber ? { ...f, stop } : f))
           );
        }
      }

      const currentStopForAction = this.load.stops.find((s) => s.stopNumber === stopNumber);
      await this.loadService.callAdvanceLoadEvent({
        loadId: this.load.id,
        actingAs: 'dispatcher',
        stopAction: action as any,
        stopNumber: stopNumber!,
        performedVia: 'admin_app',
        ...(reason ? { reason } : {}),
        ...(customTime && customTime.date && customTime.time ? { customTimestamp: customTime } : {}),
        ...(action === 'DEPART' && currentStopForAction?.detentionHours != null
          ? { detentionHours: currentStopForAction.detentionHours }
          : {}),
      });

      if (stopNumber) {
        const currentStop = this.load.stops.find((s) => s.stopNumber === stopNumber);
        this.actionDateTimeControls[stopNumber]?.setValue(this.getCurrentDateTimeObject(currentStop));
      }

      const actionLabels: Record<string, string> = {
        APPROVE: 'Documents approved',
        REJECT_DOCS: 'Documents rejected',
        WAIT_APPROVAL: 'Resubmitted for approval',
      };
      const label = actionLabels[action] || `${action} completed`;
      this.toastService.show('success', `Stop ${stopNumber}: ${label}`);
      // Refresh the load data
      this.onSuccess?.();
      // Re-read load to get updated state (parent will re-render)
      if (closeOnComplete) {
        this.modalService.close();
      }
    } catch (error: any) {
      this.toastService.show('error', error?.message || `Failed: ${action}`);
    } finally {
      this.actionLoading.set(null);
    }
  }

  async provideEta(stopNumber: number | null | undefined): Promise<void> {
    if (!stopNumber) {
      this.toastService.show('error', 'Invalid stop number');
      return;
    }
    const stopForm = this.stopForms().find((sf) => sf.stop.stopNumber === stopNumber);
    if (!stopForm) return;

    const etaValue = stopForm.form.get('etaDateTime')?.value;
    if (!etaValue || !etaValue.date || !etaValue.time) {
      this.toastService.show('error', 'Please select both date and time for the ETA');
      return;
    }

    this.actionLoading.set({ stopNumber, action: 'START_EN_ROUTE' });
    try {
      const updatedStops = [...this.load.stops];
      const stopIndex = updatedStops.findIndex((s) => s.stopNumber === stopNumber);
      if (stopIndex !== -1) {
        const stop = { ...updatedStops[stopIndex] };
        stop.etaDateTime = etaValue;
        stop.status = 'EN_ROUTE';
        updatedStops[stopIndex] = stop;

        await firstValueFrom(
          this.loadService.updateLoad(this.load.id, {
            stops: updatedStops,
            status: 'IN_TRANSIT',
          })
        );
      }

      await this.loadService.callAdvanceLoadEvent({
        loadId: this.load.id,
        actingAs: 'dispatcher',
        stopAction: 'START_EN_ROUTE',
        stopNumber,
        etaDateTime: etaValue,
        performedVia: 'admin_app',
      });

      this.toastService.show('success', `Stop ${stopNumber}: ETA provided and marked En Route`);
      this.onSuccess?.();
      this.modalService.close();
    } catch (error: any) {
      this.toastService.show('error', error?.message || 'Failed to provide ETA');
    } finally {
      this.actionLoading.set(null);
    }
  }

  async updateEta(stopNumber: number | null | undefined): Promise<void> {
    if (!stopNumber) return;
    const stopForm = this.stopForms().find((sf) => sf.stop.stopNumber === stopNumber);
    if (!stopForm) return;

    const etaValue = stopForm.form.get('etaDateTime')?.value;
    if (!etaValue || !etaValue.date || !etaValue.time) {
      this.toastService.show('error', 'Please select both date and time for the ETA');
      return;
    }

    this.actionLoading.set({ stopNumber, action: 'UPDATE_ETA' });
    try {
      const updatedStops = [...this.load.stops];
      const stopIndex = updatedStops.findIndex((s) => s.stopNumber === stopNumber);
      if (stopIndex !== -1) {
        const stop = { ...updatedStops[stopIndex] };
        stop.etaDateTime = etaValue;
        updatedStops[stopIndex] = stop;

        await firstValueFrom(
          this.loadService.updateLoad(this.load.id, {
            stops: updatedStops,
          })
        );
      }

      await this.loadService.callAdvanceLoadEvent({
        loadId: this.load.id,
        actingAs: 'dispatcher',
        stopAction: 'UPDATE_ETA',
        stopNumber,
        etaDateTime: etaValue,
        performedVia: 'admin_app',
      });

      this.toastService.show('success', `Stop ${stopNumber}: ETA updated`);
      this.stopForms.update((forms) => {
        const newForms = [...forms];
        newForms[stopIndex] = {
          ...newForms[stopIndex],
          stop: { ...newForms[stopIndex].stop, etaDateTime: etaValue },
        };
        newForms[stopIndex].editingFields.delete('eta');
        return newForms;
      });
      this.onSuccess?.();
    } catch (error: any) {
      this.toastService.show('error', error?.message || 'Failed to update ETA');
    } finally {
      this.actionLoading.set(null);
    }
  }

  cancel(): void {
    this.modalService.close();
  }
}
